import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  ShoppingBag,
  Edit,
  Eye,
  IndianRupeeIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import './AccountSections.css';
import {FaIndianRupeeSign} from "react-icons/fa6";

const MyCollabSection = ({ userId }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeCollaborations: 0,
    totalCampaigns: 0,
    creatorsWorkedWith: 0,
    totalSpent: 0
  });

  const [campaigns, setCampaigns] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyCollabData();
  }, [userId]);

  const fetchMyCollabData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats
      const statsResponse = await api.get(`/brands/${userId}/mycollab/stats`);
      setStats(statsResponse.data || {
        activeCollaborations: 0,
        totalCampaigns: 0,
        creatorsWorkedWith: 0,
        totalSpent: 0
      });

      // Fetch campaigns
      const campaignsResponse = await api.get(`/brands/${userId}/campaigns`);
      setCampaigns(Array.isArray(campaignsResponse.data) ? campaignsResponse.data : []);

      // Fetch recent activities
      const activitiesResponse = await api.get(`/brands/${userId}/mycollab/activities?limit=10`);
      setActivities(activitiesResponse.data || []);

    } catch (err) {
      setError('Failed to load MyCollab data');
      setStats({
        activeCollaborations: 0,
        totalCampaigns: 0,
        creatorsWorkedWith: 0,
        totalSpent: 0
      });
      setCampaigns([]);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Active Collaborations',
      value: stats.activeCollaborations,
      icon: <ShoppingBag size={32} />,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Total Campaigns',
      value: stats.totalCampaigns,
      icon: <TrendingUp size={32} />,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'creators Worked With',
      value: stats.creatorsWorkedWith,
      icon: <Users size={32} />,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'Total Spent',
      value: `${(stats.totalSpent || 0).toLocaleString('en-IN')}`,
      icon: <IndianRupeeIcon size={32} />,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'campaign':
        return 'bg-blue-500';
      case 'collaboration':
        return 'bg-green-500';
      case 'bid':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">MyCollab Overview</h2>
        <p className="section-description">
          Track your collaboration activities and performance metrics
        </p>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading MyCollab data...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="stats-grid">
            {statCards.map((stat, index) => (
              <div key={index} className={`stat-card bg-gradient-to-br ${stat.color}`}>
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-content">
                  <h3 className="stat-title">{stat.title}</h3>
                  <p className="stat-value">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Campaigns Section */}
          <div className="campaigns-section mt-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">My Campaigns</h3>
              <button
                onClick={() => navigate('/dashboard/brand')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View All Campaigns →
              </button>
            </div>

            {campaigns.length > 0 ? (
              <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Campaign Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Budget
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bids
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deadline
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.slice(0, 5).map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {campaign.brandLogoUrl && (
                              <img
                                src={campaign.brandLogoUrl}
                                alt={campaign.title}
                                className="h-10 w-10 rounded-full mr-3 object-cover"
                              />
                            )}
                            <div className="text-sm font-medium text-gray-900">
                              {campaign.title}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            campaign.status === 'Active' 
                              ? 'bg-green-100 text-green-800' 
                              : campaign.status === 'Completed'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{Number(campaign.budget).toLocaleString('en-IN')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {campaign.numBids || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.biddingEndDate ? new Date(campaign.biddingEndDate).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => navigate(`/dashboard/brand/campaigns/${campaign.id}`)}
                            className="text-blue-600 hover:text-blue-900 mr-3 inline-flex items-center"
                            title="View Details"
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/dashboard/brand/campaigns/${campaign.id}?edit=true`)}
                            className="text-green-600 hover:text-green-900 inline-flex items-center"
                            title="Edit Campaign"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <ShoppingBag size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 mb-4">No campaigns created yet</p>
                <button
                  onClick={() => navigate('/dashboard/brand')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Your First Campaign
                </button>
              </div>
            )}
          </div>

          <div className="recent-activity">
            <h3 className="activity-title">Recent Activity</h3>
            {activities.length > 0 ? (
              <div className="activity-list">
                {activities.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-dot ${getActivityColor(activity.activityType)}`}></div>
                    <div className="activity-content">
                      <p className="activity-text">{activity.activityText}</p>
                      <p className="activity-time">{activity.activityTime}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 p-4">No recent activities found</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MyCollabSection;


